import { useSyncExternalStore } from 'react';
import { axiosInstance } from './axiosInstance';
import { signalR } from 'src/signalR';
import { sanitizeText } from 'src/utils/sanitize';

const initSignalR = async (token: string) => {
  try {
    const baseUrl = `${axiosInstance.defaults.baseURL}/notification`;
    await signalR.ensureConnection(baseUrl, token);
  } catch (error) {
    console.error('Failed to connesct to SignalR:', error);
  }
};

interface Scope {
  client: string;
}

interface DecodedToken {
  scopes: string;
}

class ClientConfig {
  private client = '';
  private platform = '';
  private listeners: Set<() => void> = new Set();
  constructor() {
    this.checkInitialClient();
  }

  private getTargetClient() {
    const targetClient = new URLSearchParams(window.location.search).get('targetClient');
    return targetClient ? sanitizeText(targetClient) : '';
  }

  private async setNewTokenFromTargetedClient() {
    const targetClient = this.getTargetClient();
    if (!targetClient) {
      return;
    }
    const response = await axiosInstance.post(
      'token',
      {
        client: sanitizeText(targetClient),
        refreshToken: localStorage.getItem('refreshToken'),
      },
      {
        headers: {
          client: sanitizeText(targetClient),
        },
      },
    );
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    this.notifyListeners();
    this.setClient(targetClient);
    if (response.data.token) {
      initSignalR(response.data.token);
    }
  }

  private async checkInitialClient() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Needed during client switch - from subdomain client to domain client when token is not available (no data in localstorage, scopes are not available)
        // Needed to set headers for axios instance
        const targetClient = this.getTargetClient();
        if (targetClient) {
          this.setClient(targetClient);
        }
        return;
      }
      const decodedToken = JSON.parse(atob(token.split('.')[1])) as DecodedToken;
      const scopes = JSON.parse(decodedToken.scopes) as Scope[];

      if (scopes?.[0]?.client) {
        // Needed during client switch - from subdomain client to domain client - to remove old token and refresh token to avoid logining in with old client
        const targetClient = this.getTargetClient();
        if (targetClient && targetClient !== scopes[0].client) {
          this.setNewTokenFromTargetedClient();
          return;
        }
        this.client = sanitizeText(scopes[0].client);
      }
    } catch (error) {
      console.error('Error checking initial client', error);
    }
  }

  setPlatform(newPlatform: string) {
    this.platform = newPlatform;
  }

  setClient(newClient: string) {
    this.client = sanitizeText(newClient);
  }

  getPlatform() {
    return this.platform;
  }

  getClient() {
    return this.client;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }
}

export const clientConfig = new ClientConfig();

export const useClient = () => {
  return useSyncExternalStore(
    (listener) => clientConfig.subscribe(listener),
    () => clientConfig.getClient(),
  );
};
